// CalendarWeekGrid: the PA calendar's Week body, drawn as Antonin's export draws it (`src/components/CalendarGrid/
// CalendarGrid.tsx`, week view, as the practitioner Calendar screen mounts it: `screens/practitioner/CalendarScreen.tsx`
// `tone="practitioner"`): one scroller with the day header pinned to the top and the hour gutter to the left, 24-hour
// labels, an hour line every 80 px and a dashed half-hour line, today's header on the blue-100 band and its column
// tinted, the now line on today's column, and each booking an export tile (`WeekAppointmentTile`) at its start and
// length, concurrent ones side by side.
// It takes the SAME props as the Signal DS `PACalendarWeek` and keeps every behaviour the page uses from it: click a
// tile (`onAppointmentClick`), click a slot (`onSlotClick`), drag a tile onto a half-hour cell of its own day to
// reschedule (`onAppointmentDrop`, the DS payload, built by the DS mechanism: dnd-kit, a 6 px activation distance and
// one droppable per half hour), the `hours` gutter, block tiles and the overlap layout. It composes the DS pieces the
// DS Week composes: `PACalendarGutterClock` (corner), `PACalendarDayColumnHeader` (day heads),
// `PACalendarCurrentTimeLine` (now line).
// Stand-in for `PACalendarWeek` (PROD-3310): the DS Week has no tile slot and its tile carries no stable id.
// Look: `src/styles/showcase-calendar-week.css`. Runtime geometry is passed as custom properties only.
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import {
  PACalendarCurrentTimeLine,
  PACalendarDayColumnHeader,
  PACalendarGutterClock,
  type PACalendarAppointment,
  type PACalendarWeekProps,
} from '@scripsteam/scrips-signal-ds';
import { practiceNow } from '@/shared/lib/practice-time';
import { WeekAppointmentTile } from './week-appointment-tile';
import {
  halfCellId,
  hourMark,
  isTilePast,
  layoutOverlaps,
  tileGeometry,
  WEEK_DEFAULT_HOURS,
  WEEK_HOUR_PX,
  weekDropPayload,
} from './week-layout';

export function CalendarWeekGrid({ days, appointments, hours, onAppointmentClick, onSlotClick, onAppointmentDrop }: PACalendarWeekProps) {
  const visibleHours = hours && hours.length > 0 ? hours : WEEK_DEFAULT_HOURS;
  const firstHour = visibleHours[0];
  const lastHour = visibleHours[visibleHours.length - 1];
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const byId = useMemo(() => new Map(appointments.map((a) => [a.id, a])), [appointments]);
  const now = practiceNow();

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onAppointmentDrop) return;
    const payload = weekDropPayload(byId.get(String(event.active.id)), event.over?.id);
    if (payload) onAppointmentDrop(payload);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="cal-week" data-slot="pa-calendar-week">
        <div className="cal-week-scroll" data-cal-scroll="">
          <div className="cal-week-sheet">
            <div className="cal-week-head">
              <PACalendarGutterClock className="cal-week-corner" />
              {days.map((d, i) => (
                <div key={d.dateISO ?? i} className="cal-week-day" role="group" aria-label={`${d.dayOfWeek} ${d.date}`} data-today={d.isToday ? '' : undefined}>
                  {/* The DS header runs the weekday and the date together ("Tue20"; the look's space is CSS), so the
                      group carries the real name "Tue 20" and the visual header is hidden from assistive tech. */}
                  <div className="cal-week-day-visual" aria-hidden>
                    <PACalendarDayColumnHeader className="cal-week-day-head" dayOfWeek={d.dayOfWeek} date={d.date} isToday={d.isToday} />
                  </div>
                  {d.isToday && <span className="cal-week-today-word">Today</span>}
                </div>
              ))}
            </div>
            <div className="cal-week-body">
              <div className="cal-week-gutter">
                {visibleHours.map((h) => (
                  <div key={h} className="cal-week-hour-label">
                    {hourMark(h)}
                  </div>
                ))}
              </div>
              {days.map((d, dayIndex) => {
                const mine = appointments.filter((a) => a.dayIndex === dayIndex);
                const lay = layoutOverlaps(mine);
                return (
                  <div key={d.dateISO ?? dayIndex} className="cal-week-col" data-today={d.isToday ? '' : undefined}>
                    {visibleHours.map((h) => (
                      <HourCell key={h} dayIndex={dayIndex} hour={h} onSlotClick={onSlotClick} />
                    ))}
                    <div className="cal-week-layer">
                      {mine.map((apt) => {
                        const slot = lay.get(apt.id);
                        const overlap = slot && slot.width < 100 ? slot : undefined;
                        return (
                          <DraggableTile
                            key={apt.id}
                            appointment={apt}
                            firstHour={firstHour}
                            overlap={overlap}
                            past={isTilePast(apt, now)}
                            draggable={!!onAppointmentDrop}
                            onClick={() => onAppointmentClick?.(apt.id)}
                          />
                        );
                      })}
                      {d.isToday ? (
                        <PACalendarCurrentTimeLine className="cal-week-now" startHour={firstHour} endHour={lastHour + 1} hourHeight={WEEK_HOUR_PX} />
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  );
}

/** One hour of a day column: two half-hour drop targets (DS `DayHourCell` / `HalfHourCell`), the dashed line between. */
function HourCell({ dayIndex, hour, onSlotClick }: { dayIndex: number; hour: number; onSlotClick?: (dayIndex: number, hour: number) => void }) {
  return (
    <div className="cal-week-hour" data-hour={hour} onClick={() => onSlotClick?.(dayIndex, hour)}>
      <HalfCell id={halfCellId(dayIndex, hour, 0)} />
      <HalfCell id={halfCellId(dayIndex, hour, 1)} bottom />
    </div>
  );
}

function HalfCell({ id, bottom = false }: { id: string; bottom?: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} className="cal-week-half" data-half={bottom ? 'bottom' : 'top'} data-over={isOver ? '' : undefined} />;
}

/** A tile in its slot: the slot carries the runtime geometry (custom properties) and the drag transform; the tile's own
 *  button is the drag activator (one control, one tab stop). */
function DraggableTile({
  appointment,
  firstHour,
  overlap,
  past,
  draggable,
  onClick,
}: {
  appointment: PACalendarAppointment;
  firstHour: number;
  overlap?: { left: number; width: number };
  past: boolean;
  draggable: boolean;
  onClick: () => void;
}): ReactNode {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } = useDraggable({ id: appointment.id, disabled: !draggable });
  const { top, height } = tileGeometry(appointment, firstHour);
  return (
    <div
      ref={draggable ? setNodeRef : undefined}
      className="cal-week-slot"
      data-overlap={overlap ? '' : undefined}
      data-dragging={isDragging ? '' : undefined}
      style={
        {
          '--cal-tile-top': `${top}px`,
          '--cal-tile-height': `${height}px`,
          '--cal-tile-left': overlap ? `${overlap.left}%` : undefined,
          '--cal-tile-width': overlap ? `${overlap.width}%` : undefined,
          '--cal-drag-x': transform ? `${transform.x}px` : undefined,
          '--cal-drag-y': transform ? `${transform.y}px` : undefined,
        } as CSSProperties
      }
    >
      <WeekAppointmentTile
        appointment={appointment}
        height={height}
        past={past}
        onClick={onClick}
        dragHandle={draggable ? { ref: setActivatorNodeRef, attributes, listeners } : undefined}
      />
    </div>
  );
}
